import React from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Paper, Box } from '@mui/material';

/**
 * TabsNav - Reusable sleek dashboard-style tabs with underline indicator
 * Usage:
 *   <TabsNav value={active} onChange={setActive} items={[{label:'General', icon:<Icon/>}, ...]} />
 */
const TabsNav = ({ value, onChange, items, sx, paperProps, tabsProps }) => {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        px: 1,
        py: 0.5,
        bgcolor: 'background.paper',
        ...paperProps?.sx,
      }}
    >
      <Tabs
        value={value}
        onChange={(_, v) => onChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          fontFamily: 'inherit',
          minHeight: 44,
          '& .MuiTabs-flexContainer': { gap: 0.5 },
          '& .MuiTabs-indicator': {
            height: 2,
            borderRadius: 2,
            bgcolor: 'primary.main',
            transition: 'all .25s ease',
          },
          ...sx,
        }}
        {...tabsProps}
      >
        {items.map((it, idx) => (
          <Tab
            key={idx}
            label={it.label}
            icon={it.icon || null}
            iconPosition={it.icon ? 'start' : undefined}
            disableRipple
            sx={{
              fontFamily: 'inherit',
              textTransform: 'none',
              fontWeight: 600,
              letterSpacing: 0.2,
              minHeight: 44,
              px: 1.5,
              color: 'text.secondary',
              transition: 'color .2s ease',
              '& .MuiTab-iconWrapper': {
                mr: it.icon ? 1 : 0,
                color: 'action.active',
                transition: 'color .2s ease, transform .2s ease',
              },
              '&:hover': {
                color: 'text.primary',
                '& .MuiTab-iconWrapper': { color: 'text.primary', transform: 'translateY(-1px)' },
                backgroundColor: 'action.hover',
                borderRadius: 1,
              },
              '&.Mui-selected': {
                color: 'text.primary',
                '& .MuiTab-iconWrapper': { color: 'primary.main' },
              },
            }}
          />
        ))}
      </Tabs>
      {/* Tiny divider under tabs */}
      <Box sx={{ height: 1, bgcolor: 'divider', mt: 0.5, borderRadius: 1 }} />
    </Paper>
  );
};

TabsNav.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
    })
  ).isRequired,
  sx: PropTypes.object,
  paperProps: PropTypes.object,
  tabsProps: PropTypes.object,
};

export default TabsNav;
